import { tr, uiLocale, getCurrentLang } from "../i18n/LanguageContext";
import { setPageMeta, summarize } from "../i18n/seo";
import React, { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { useNavigate } from "../i18n/router";
import { MapPin, Calendar, Clock, Users, MessageCircle, ArrowRight, Loader2, Star, UserPlus, UserCheck } from "lucide-react";
import { useAuth } from "../contexts/AuthContext";
import { useToast } from "../hooks/use-toast";
import EventTypeBadge from "../components/EventTypeBadge";
import MessageDialog from "../components/MessageDialog";
import { getPracticeLabel } from "../data/mock";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const OrganizerProfilePage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { toast } = useToast();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showMessage, setShowMessage] = useState(false);
  const [isFollowing, setIsFollowing] = useState(false);
  const [followerCount, setFollowerCount] = useState(0);
  const [followBusy, setFollowBusy] = useState(false);

  useEffect(() => {
    const org = data?.organizer;
    if (!org) return;
    setPageMeta({ title: `${org.name} — ${tr("Организатор")} | EUNOIA`, description: summarize(org.bio) });
  }, [data]);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const res = await fetch(`${BACKEND_URL}/api/organizers/${id}`);
        if (!res.ok) throw new Error(tr("Не найден"));
        const d = await res.json();
        setData(d);
        // Fetch follow status / counter using the organizer's userId
        const uid = d?.organizer?.userId;
        if (uid) {
          try {
            const fr = await fetch(`${BACKEND_URL}/api/follow/${uid}/status`, { credentials: "include" });
            if (fr.ok) {
              const fd = await fr.json();
              setIsFollowing(!!fd.isFollowing);
              setFollowerCount(fd.followerCount || 0);
            } else if (fr.status === 401) {
              // Guest — fetch public count only
              const cr = await fetch(`${BACKEND_URL}/api/follow/${uid}/count`);
              if (cr.ok) setFollowerCount((await cr.json()).followerCount || 0);
            }
          } catch { /* ignore */ }
        }
      } catch {
        setData(null);
      } finally {
        setLoading(false);
      }
    })();
  }, [id]);

  const handleContact = () => {
    if (!user) { navigate(`/login?redirect=/organizers/${id}`); return; }
    setShowMessage(true);
  };

  const isSelf = user && data?.organizer?.userId === user.id;

  const handleToggleFollow = async () => {
    if (!user) {
      navigate(`/login?redirect=/organizers/${id}&intent=follow_organizer`);
      return;
    }
    if (followBusy) return;
    const uid = data?.organizer?.userId;
    if (!uid) return;

    // Optimistic
    const prev = isFollowing;
    const prevCount = followerCount;
    setIsFollowing(!prev);
    setFollowerCount((c) => Math.max(0, c + (prev ? -1 : 1)));
    setFollowBusy(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/follow/${uid}`, {
        method: prev ? "DELETE" : "POST",
        credentials: "include",
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      toast({ title: prev ? tr("Вы отписались") : tr("Вы подписались") });
    } catch (e) {
      console.error("[follow] failed", e);
      setIsFollowing(prev);
      setFollowerCount(prevCount);
      toast({
        title: prev ? tr("Не удалось отписаться") : tr("Не удалось подписаться"),
        description: tr("Попробуйте снова."),
        variant: "destructive",
      });
    } finally {
      setFollowBusy(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin" style={{ color: "var(--eunoia-accent)" }} />
      </div>
    );
  }

  if (!data) {
    return (
      <div className="min-h-screen eunoia-space-lg px-4">
        <div className="max-w-2xl mx-auto text-center py-20">
          <h1 className="eunoia-title-large mb-4">{tr("Организатор не найден")}</h1>
          <button onClick={() => navigate("/organizers")} className="eunoia-btn-primary">{tr("Все организаторы")}</button>
        </div>
      </div>
    );
  }

  const org = data.organizer;
  const events = data.events || [];
  const now = new Date().toISOString().split("T")[0];
  const upcoming = events.filter((e) => e.date >= now);
  const past = events.filter((e) => e.date < now);

  return (
    <div className="min-h-screen eunoia-space-lg pb-16" data-testid="organizer-profile-page">
      <div className="max-w-4xl mx-auto px-4 sm:px-6">

        {/* Hero / Cover */}
        <div className="eunoia-surface overflow-hidden mb-8">
          {/* Cover gradient */}
          <div className="h-36 sm:h-48" style={{ background: "linear-gradient(135deg, rgba(189,162,122,0.15) 0%, rgba(189,162,122,0.05) 100%)" }} />

          {/* Profile section overlapping cover */}
          <div className="px-6 sm:px-10 pb-8 -mt-16 sm:-mt-20">
            <div className="flex flex-col sm:flex-row items-start gap-5">
              {/* Avatar */}
              <div className="w-28 h-28 sm:w-36 sm:h-36 rounded-full flex-shrink-0 overflow-hidden ring-4 ring-white"
                style={{ backgroundColor: "var(--eunoia-accent-whisper)" }}>
                {org.image ? (
                  <img src={org.image} alt={org.name} className="w-full h-full object-cover" />
                ) : (
                  <div className="w-full h-full flex items-center justify-center">
                    <span className="text-4xl font-medium" style={{ color: "var(--eunoia-accent)" }}>
                      {(org.name || "?").split(" ").map((n) => n[0]).join("")}
                    </span>
                  </div>
                )}
              </div>

              {/* Name + meta */}
              <div className="flex-1 pt-2 sm:pt-8">
                <h1 className="eunoia-title-large mb-1" data-testid="org-name">{org.name}</h1>
                {org.city && (
                  <p className="eunoia-caption flex items-center gap-1.5 mb-3">
                    <MapPin className="h-4 w-4" style={{ color: "var(--eunoia-accent)" }} />{org.city}
                  </p>
                )}
                <div className="flex flex-wrap items-center gap-2">
                  <button onClick={handleContact} className="eunoia-btn-primary flex items-center gap-2 text-sm" data-testid="contact-org-btn">
                    <MessageCircle className="h-4 w-4" /> {tr("Написать организатору")}
                  </button>
                  {!isSelf && (
                    <button
                      onClick={handleToggleFollow}
                      disabled={followBusy}
                      data-testid="follow-org-btn"
                      aria-pressed={isFollowing}
                      className="flex items-center gap-2 text-sm px-5 py-2.5 rounded-full border-2 transition-all hover:bg-white disabled:opacity-60"
                      style={{
                        borderColor: "var(--eunoia-accent)",
                        color: isFollowing ? "var(--eunoia-soft)" : "var(--eunoia-accent)",
                        backgroundColor: isFollowing ? "var(--eunoia-accent-whisper)" : "transparent",
                      }}
                    >
                      {followBusy ? <Loader2 className="h-4 w-4 animate-spin" />
                        : isFollowing ? <UserCheck className="h-4 w-4" /> : <UserPlus className="h-4 w-4" />}
                      {isFollowing ? tr("Вы подписаны") : tr("Подписаться")}
                    </button>
                  )}
                  {followerCount > 0 && (
                    <span className="text-xs ml-1" style={{ color: "var(--eunoia-soft)" }} data-testid="follower-count">
                      {followerCount} {getCurrentLang() === "en" ? (followerCount === 1 ? "follower" : "followers") : followerCount === 1 ? "подписчик" : followerCount < 5 ? "подписчика" : "подписчиков"}
                    </span>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* About */}
        {org.bio && (
          <div className="eunoia-surface p-6 sm:p-8 mb-6">
            <h2 className="eunoia-title-small mb-4">{tr("Об организаторе")}</h2>
            <p className="eunoia-body whitespace-pre-wrap" data-testid="org-bio">{org.bio}</p>
          </div>
        )}

        {/* Practices */}
        {org.practices && org.practices.length > 0 && (
          <div className="eunoia-surface p-6 sm:p-8 mb-6">
            <h2 className="eunoia-title-small mb-4">{tr("Направления практик")}</h2>
            <div className="flex flex-wrap gap-2">
              {org.practices.map((p, i) => (
                <span key={i} data-testid={`profile-practice-${i}`} className="px-4 py-2 rounded-full text-sm"
                  style={{ backgroundColor: "var(--eunoia-accent-whisper)", color: "var(--eunoia-accent)" }}>{getPracticeLabel(p)}</span>
              ))}
            </div>
          </div>
        )}

        {/* Upcoming Events */}
        <div className="mb-6">
          <h2 className="eunoia-title-small mb-4 flex items-center gap-2">
            <Calendar className="h-5 w-5" style={{ color: "var(--eunoia-accent)" }} />
            {tr("Предстоящие события")}
            {upcoming.length > 0 && (
              <span className="px-2 py-0.5 rounded-full text-xs font-medium" style={{ backgroundColor: "var(--eunoia-accent)", color: "white" }}>{upcoming.length}</span>
            )}
          </h2>
          {upcoming.length === 0 ? (
            <div className="eunoia-surface p-8 text-center">
              <p className="eunoia-caption">{tr("Нет предстоящих событий")}</p>
            </div>
          ) : (
            <div className="space-y-3">
              {upcoming.map((ev) => (
                <EventCard key={ev.id} event={ev} navigate={navigate} />
              ))}
            </div>
          )}
        </div>

        {/* Past Events */}
        {past.length > 0 && (
          <div className="mb-6">
            <h2 className="eunoia-title-small mb-4 flex items-center gap-2" style={{ color: "var(--eunoia-soft)" }}>
              <Clock className="h-5 w-5" /> {tr("Прошедшие события")}
            </h2>
            <div className="space-y-3 opacity-70">
              {past.slice(0, 5).map((ev) => (
                <EventCard key={ev.id} event={ev} navigate={navigate} />
              ))}
            </div>
          </div>
        )}

        {/* Reviews — future-ready empty state */}
        <div className="eunoia-surface p-6 sm:p-8 mb-6">
          <h2 className="eunoia-title-small mb-4 flex items-center gap-2">
            <Star className="h-5 w-5" style={{ color: "var(--eunoia-accent)" }} /> {tr("Отзывы")}
          </h2>
          <div className="text-center py-6">
            <Star className="h-10 w-10 mx-auto mb-3" style={{ color: "var(--eunoia-whisper)" }} />
            <p className="eunoia-caption">{tr("Отзывы появятся скоро")}</p>
          </div>
        </div>
      </div>

      {showMessage && <MessageDialog organizer={org} onClose={() => setShowMessage(false)} />}
    </div>
  );
};

function EventCard({ event, navigate }) {
  return (
    <div
      className="eunoia-surface p-5 flex items-start gap-4 hover:-translate-y-0.5 transition-all cursor-pointer group"
      onClick={() => navigate(`/events/${event.id}`)}
      data-testid={`event-card-${event.id}`}
    >
      {/* Date block */}
      <div className="w-14 h-14 rounded-xl flex-shrink-0 flex flex-col items-center justify-center"
        style={{ backgroundColor: "var(--eunoia-accent-whisper)" }}>
        <span className="text-lg font-bold leading-none" style={{ color: "var(--eunoia-accent)" }}>
          {new Date(event.date).getDate()}
        </span>
        <span className="text-[10px] uppercase mt-0.5" style={{ color: "var(--eunoia-soft)" }}>
          {new Date(event.date).toLocaleDateString(uiLocale(), { month: "short" })}
        </span>
      </div>

      {/* Info */}
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 mb-1 flex-wrap">
          <h3 className="text-sm font-semibold truncate group-hover:underline" style={{ color: "var(--eunoia-deep)" }}>{event.title}</h3>
          <EventTypeBadge type={event.type} />
        </div>
        <div className="flex items-center gap-3 text-xs" style={{ color: "var(--eunoia-soft)" }}>
          <span className="flex items-center gap-1"><Clock className="h-3 w-3" />{event.time}</span>
          {event.city && <span className="flex items-center gap-1"><MapPin className="h-3 w-3" />{event.city}</span>}
        </div>
      </div>
      <ArrowRight className="h-4 w-4 flex-shrink-0 opacity-0 group-hover:opacity-100 transition-opacity" style={{ color: "var(--eunoia-accent)" }} />
    </div>
  );
}

export default OrganizerProfilePage;
